import React from 'react';
import { MOCK_RECORDINGS } from '../mockData';

function StatCard({ icon, label, subtitle, color, borderColor }) {
  return (
    <div className="stat-card" style={{ borderTopColor: borderColor }}>
      <div className="stat-card-top">
        <div className={`stat-card-icon-box`} style={{ background: `${borderColor}18`, color: borderColor }}>
          {icon}
        </div>
      </div>
      <div className="mt-3">
        <p className="text-[13px] text-[var(--adani-text-secondary)] leading-snug">{label}</p>
        <p className="text-[12px] text-[var(--adani-primary)] mt-1 font-medium">{subtitle}</p>
      </div>
    </div>
  );
}

function SectionCard({ title, badge, children, className = '' }) {
  return (
    <div className={`glass-card-static rounded-2xl overflow-hidden ${className}`}>
      <div className="flex items-center justify-between px-5 pt-5 pb-0">
        <h3 className="font-google text-[15px] font-semibold text-[var(--adani-text-primary)]">{title}</h3>
        {badge !== undefined && (
          <span className="badge badge-primary text-[11px]">{badge}</span>
        )}
      </div>
      <div className="px-5 pb-5 pt-3">
        {children}
      </div>
    </div>
  );
}

function RootCauseRow({ icon, label }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-[var(--adani-border)] last:border-0">
      <div className="flex items-center gap-2.5">
        <span className="text-sm">{icon}</span>
        <span className="text-[13px] text-[var(--adani-text-secondary)]">{label}</span>
      </div>
      <span className="text-[13px] text-[var(--adani-text-tertiary)]">—</span>
    </div>
  );
}

function DelayBar({ label, colors }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="flex gap-0.5">
        {colors.map((c, i) => (
          <div key={i} className="w-5 h-5 rounded-sm" style={{ background: c }} />
        ))}
      </div>
      <span className="text-[11px] text-[var(--adani-text-tertiary)]">{label}</span>
    </div>
  );
}

export default function DashboardPage({ counts = {} }) {
  const stats = {
    totalRecordings: counts.recordings || 0,
    totalLeads: counts.leads || 0,
    insights: counts.insights || 0,
    milestones: counts.milestones || 0,
    bottlenecks: counts.bottlenecks || 0,
  };
  const recent = MOCK_RECORDINGS.filter(r => r.status === 'Completed').slice(0, 3);

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-google text-[22px] font-semibold text-[var(--adani-text-primary)] leading-tight">
            Executive Dashboard
          </h1>
          <p className="text-[13px] text-[var(--adani-text-secondary)] mt-1">
            Real-time overview of captured programme data
          </p>
        </div>
        <button className="btn-secondary text-[13px] py-2 px-3.5">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
          Refresh
        </button>
      </div>

      {/* Stat Cards - 4 columns */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>}
          label="Total Recordings"
          subtitle={stats.totalRecordings > 0 ? `${stats.totalRecordings} recorded` : 'No recordings yet'}
          borderColor="#00A3E0"
        />
        <StatCard
          icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/></svg>}
          label="Total Leads"
          subtitle={stats.totalLeads > 0 ? `${stats.totalLeads} captured` : 'No leads yet'}
          borderColor="#2E6DE5"
        />
        <StatCard
          icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 19.5h20L12 2z"/></svg>}
          label="Insights"
          subtitle={stats.insights > 0 ? `${stats.insights} insights` : 'No insights yet'}
          borderColor="#6C3BBF"
        />
        <StatCard
          icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>}
          label="Milestones"
          subtitle={stats.milestones > 0 ? `${stats.milestones} tracked` : 'No milestones yet'}
          borderColor="#D42789"
        />
      </div>

      {/* Recent Captures + Programme Health - 2 columns */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <SectionCard title="Recent Captures" badge={stats.totalRecordings > 0 ? stats.totalRecordings : undefined}>
          {recent.length > 0 ? (
            <div className="space-y-3 mt-1">
              {recent.map(r => (
                <div key={r.id} className="flex items-start gap-3">
                  <div className="status-dot status-dot-success mt-1.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-medium text-[var(--adani-text-primary)] truncate">{r.title}</p>
                    <p className="text-[11px] text-[var(--adani-text-tertiary)] mt-0.5">{r.date} · {r.type}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[13px] text-[var(--adani-text-tertiary)] text-center py-6">No captures yet.</p>
          )}
        </SectionCard>

        <SectionCard title="Programme Health Overview">
          <p className="text-[13px] text-[var(--adani-text-tertiary)] text-center py-6">No programmes added yet.</p>
        </SectionCard>
      </div>

      {/* AI Insights + Delay Distribution + 5W Root Cause - 3 columns */}
      <div className="grid grid-cols-3 gap-4">
        <SectionCard title="AI Insights" badge="Live">
          <div className="mt-1 border-l-3 border-[var(--adani-primary)] pl-3">
            <p className="text-[13px] font-medium text-[var(--adani-text-primary)] flex items-center gap-1.5">
              <span>💡</span> Data Required
            </p>
            <p className="text-[12px] text-[var(--adani-text-tertiary)] mt-1 leading-relaxed">
              Add programmes and captures to generate AI-powered insights on risks, delays, and bottlenecks.
            </p>
          </div>
        </SectionCard>

        <SectionCard title="Delay Distribution">
          <div className="flex items-end justify-center gap-6 pt-2 pb-1">
            <DelayBar label="Q1" colors={['#00A3E0', '#00A3E0']} />
            <DelayBar label="Q2" colors={['#2E6DE5', '#2E6DE5']} />
            <DelayBar label="Q3" colors={['#6C3BBF', '#6C3BBF']} />
            <DelayBar label="Q4" colors={['#D42789', '#D42789']} />
          </div>
        </SectionCard>

        <SectionCard title="5W Root Cause Summary">
          <div className="mt-1">
            <RootCauseRow icon="👥" label="People Issues" />
            <RootCauseRow icon="⚙️" label="Process Issues" />
            <RootCauseRow icon="✨" label="Technology" />
            <RootCauseRow icon="🔗" label="External Deps" />
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
